// Vista unificada de Compradores - Tarjetas compactas tipo fila
// Reemplaza: BuyerEngagementCard + ProfilesView + CompradoresView

import React, { useState, useMemo } from 'react';
import {
  Users,
  Download,
  CheckCircle,
  Flame,
  Eye,
  Clock,
  Filter,
  ChevronRight,
  Calendar,
  CreditCard,
  Building,
  Home,
  UserCheck,
  UserX,
  UserMinus,
  Activity,
  MessageCircle,
  Target,
  Trophy,
  Info,
  X
} from 'lucide-react';
import { BuyerDetailModal } from './BuyerDetailModal';
import { maskEmail, maskPhone } from '../../../utils/maskSensitiveData';
import { getTimeInfo } from '../../../utils/engagementTimeFormatters';
import { CSV_SEPARATOR, CSV_BOM } from '../../../constants/csvConfig';
import { calculateFitPercentage } from '../../../services/propertyFit/calculateBuyerFit';

// Labels para datos de perfil
const LABELS = {
  paymentMethod: {
    cash: 'Contado',
    finance_under_60: 'Fin. <60%',
    finance_60_80: 'Fin. 60-80%',
    finance_over_80: 'Fin. >80%',
    undecided: 'Por decidir',
  },
  financingStatus: {
    preapproved: 'Preaprobada',
    in_process: 'En proceso',
    not_started: 'No iniciada',
    no_need: 'No necesaria',
  },
  propertyToSell: {
    no: 'No',
    signed_deposit: 'Arras',
    on_sale: 'En venta',
    not_yet_listed: 'Sin listar',
  },
};

// Colores para campos financieros (verde=bueno, ámbar=intermedio, rojo=riesgo)
const FINANCIAL_COLORS = {
  green: 'bg-green-500/20 border border-green-500/30 text-green-400',
  amber: 'bg-amber-500/20 border border-amber-500/30 text-amber-400',
  red: 'bg-red-500/20 border border-red-500/30 text-red-400',
  neutral: 'bg-slate-700/60 text-slate-300',
};

// Método de pago: contado y <60% verde, 60-80% y >80% ámbar, sin decidir rojo
const getPaymentColor = (method) => {
  if (method === 'cash' || method === 'finance_under_60') return FINANCIAL_COLORS.green;
  if (method === 'finance_60_80' || method === 'finance_over_80') return FINANCIAL_COLORS.amber;
  if (method === 'undecided') return FINANCIAL_COLORS.red;
  return FINANCIAL_COLORS.neutral;
};

// Financiación: preaprobada y no necesaria verde, en proceso ámbar, no iniciada rojo
const getFinancingColor = (status) => {
  if (status === 'preapproved' || status === 'no_need') return FINANCIAL_COLORS.green;
  if (status === 'in_process') return FINANCIAL_COLORS.amber;
  if (status === 'not_started') return FINANCIAL_COLORS.red;
  return FINANCIAL_COLORS.neutral;
};

// Vender vivienda: no verde, arras ámbar, en venta y sin listar rojo
const getPropertyToSellColor = (status) => {
  if (status === 'no') return FINANCIAL_COLORS.green;
  if (status === 'signed_deposit') return FINANCIAL_COLORS.amber;
  if (status === 'on_sale' || status === 'not_yet_listed') return FINANCIAL_COLORS.red;
  return FINANCIAL_COLORS.neutral;
};

/**
 * Vista de Compradores con tarjetas compactas tipo fila
 */
export const BuyersView = ({ buyerSummaries, propertyId, property, isSpectatorMode = false }) => {
  // Extraer metadata de la propiedad para cálculo de fit
  const propertyMetadata = property?.property_metadata || property?.propertyMetadata || null;
  const [sortBy, setSortBy] = useState('interestScore');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filter, setFilter] = useState('all');
  const [fitFilter, setFitFilter] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);

  // ============================================
  // FILTRAR DATOS
  // ============================================
  const filteredData = useMemo(() => {
    let data = [...buyerSummaries];

    switch (filter) {
      case 'bid':
        data = data.filter(b => b.hasBid);
        break;
      case 'attempted':
        data = data.filter(b => b.conversionStatus === 'attempted' && !b.hasBid);
        break;
      case 'watching':
        data = data.filter(b => b.visits > 0 && b.conversionStatus !== 'attempted' && !b.hasBid);
        break;
      case 'noActivity':
        data = data.filter(b => b.visits === 0);
        break;
      default:
        break;
    }

    if (fitFilter) {
      data = data.filter(b => b.profile?.fitPercentage >= 80);
    }

    return data;
  }, [buyerSummaries, filter, fitFilter]);

  // ============================================
  // ORDENAR DATOS
  // ============================================
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aVal, bVal;

      if (sortBy === 'interestScore') {
        aVal = a.interestScore ?? a.score ?? 0;
        bVal = b.interestScore ?? b.score ?? 0;
      } else if (sortBy === 'fitPercentage') {
        aVal = a.profile?.fitPercentage ?? -1;
        bVal = b.profile?.fitPercentage ?? -1;
      } else if (sortBy === 'lastVisit') {
        aVal = a.lastVisit ? new Date(a.lastVisit).getTime() : 0;
        bVal = b.lastVisit ? new Date(b.lastVisit).getTime() : 0;
      } else {
        aVal = a[sortBy] ?? 0;
        bVal = b[sortBy] ?? 0;
      }

      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [filteredData, sortBy, sortOrder]);

  // ============================================
  // EXPORT CSV
  // ============================================
  const handleExportCSV = () => {
    const headers = ['#', 'Nombre', 'Email', 'Estado', 'Interés', 'Visitas', 'Días', 'Tiempo', 'Eventos', 'Intentos', 'Pujas', 'Max Puja €', 'Fit %', 'Pago', 'Financiación', 'Vender', 'Última visita'];
    const rows = sortedData.map((buyer, index) => {
      const status = buyer.hasBid ? 'Pujó' :
        buyer.conversionStatus === 'attempted' ? 'Intentó' :
          buyer.visits > 0 ? 'Visitó' : 'Sin actividad';
      const displayEmail = isSpectatorMode ? maskEmail(buyer.email) : buyer.email;
      const profile = buyer.profile;

      return [
        index + 1,
        buyer.name,
        displayEmail,
        status,
        buyer.interestScore ?? buyer.score ?? 0,
        buyer.visits ?? 0,
        buyer.uniqueDays ?? 0,
        buyer.totalTime ?? 0,
        buyer.eventCount ?? 0,
        buyer.biddingAttempts ?? 0,
        buyer.bidsCount ?? 0,
        buyer.maxBidAmount ?? '-',
        profile?.fitPercentage ?? '-',
        profile ? LABELS.paymentMethod[profile.paymentMethod] || '-' : '-',
        profile ? LABELS.financingStatus[profile.financingStatus] || '-' : '-',
        profile ? LABELS.propertyToSell[profile.propertyToSell] || '-' : '-',
        buyer.lastVisit ? new Date(buyer.lastVisit).toLocaleDateString('es-ES') : '-',
      ];
    });

    const csvContent = [
      headers.join(CSV_SEPARATOR),
      ...rows.map(row => row.join(CSV_SEPARATOR))
    ].join('\n');

    const blob = new Blob([CSV_BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `buyers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============================================
  // CONTADORES
  // ============================================
  const counts = useMemo(() => ({
    all: buyerSummaries.length,
    bid: buyerSummaries.filter(b => b.hasBid).length,
    attempted: buyerSummaries.filter(b => b.conversionStatus === 'attempted' && !b.hasBid).length,
    watching: buyerSummaries.filter(b => b.visits > 0 && b.conversionStatus !== 'attempted' && !b.hasBid).length,
    noActivity: buyerSummaries.filter(b => b.visits === 0).length,
    fitHigh: buyerSummaries.filter(b => b.profile?.fitPercentage >= 80).length,
  }), [buyerSummaries]);

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-4">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-400" />
            Compradores
            <span className="text-slate-400 text-sm font-normal ml-2">
              ({sortedData.length} de {buyerSummaries.length})
            </span>
          </h3>
        </div>

        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 bg-amber-500 text-slate-900 px-4 py-2 rounded-lg hover:bg-amber-600 transition-all font-semibold text-sm"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* FILTROS */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-slate-400" />
        <FilterChip label={`Todos (${counts.all})`} active={filter === 'all'} onClick={() => setFilter('all')} />
        <FilterChip label={`Pujaron (${counts.bid})`} active={filter === 'bid'} onClick={() => setFilter('bid')} color="green" />
        <FilterChip label={`Intentaron (${counts.attempted})`} active={filter === 'attempted'} onClick={() => setFilter('attempted')} color="orange" />
        <FilterChip label={`Visitaron (${counts.watching})`} active={filter === 'watching'} onClick={() => setFilter('watching')} color="blue" />
        <FilterChip label={`Sin actividad (${counts.noActivity})`} active={filter === 'noActivity'} onClick={() => setFilter('noActivity')} color="slate" />
        <div className="w-px h-6 bg-slate-600 mx-2" />
        <FilterChip label={`Fit ≥80% (${counts.fitHigh})`} active={fitFilter} onClick={() => setFitFilter(!fitFilter)} color="purple" />
      </div>

      {/* LISTA DE TARJETAS-FILA */}
      <div className="space-y-2">
        {sortedData.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-800/50 rounded-xl">
            No hay compradores que coincidan con los filtros
          </div>
        ) : (
          sortedData.map((buyer, index) => (
            <BuyerCard
              key={buyer.email}
              buyer={buyer}
              rank={index + 1}
              isSpectatorMode={isSpectatorMode}
              onViewDetail={() => setSelectedBuyer(buyer)}
              propertyMetadata={propertyMetadata}
            />
          ))
        )}
      </div>

      {/* MODAL DETALLE */}
      {selectedBuyer && (
        <BuyerDetailModal
          buyer={selectedBuyer}
          propertyId={propertyId}
          onClose={() => setSelectedBuyer(null)}
        />
      )}
    </div>
  );
};

// ============================================
// COMPONENTES AUXILIARES
// ============================================

const FilterChip = ({ label, active, onClick, color = 'amber' }) => {
  const colors = {
    amber: active ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' : 'border-slate-600 text-slate-400 hover:border-slate-500',
    green: active ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'border-slate-600 text-slate-400 hover:border-slate-500',
    orange: active ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'border-slate-600 text-slate-400 hover:border-slate-500',
    blue: active ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'border-slate-600 text-slate-400 hover:border-slate-500',
    slate: active ? 'bg-slate-500/20 border-slate-500/50 text-slate-300' : 'border-slate-600 text-slate-400 hover:border-slate-500',
    purple: active ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' : 'border-slate-600 text-slate-400 hover:border-slate-500',
  };

  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${colors[color]}`}
    >
      {label}
    </button>
  );
};

/**
 * Tarjeta compacta de comprador (estilo fila pero más visual)
 * Layout: 2 bloques principales - Engagement (izq) | Perfil financiero (der)
 */
const BuyerCard = ({ buyer, rank, isSpectatorMode, onViewDetail, propertyMetadata }) => {
  const [showFitPopover, setShowFitPopover] = useState(false);
  const [fitCalculation, setFitCalculation] = useState(null);
  const [popoverDirection, setPopoverDirection] = useState('down'); // 'up' o 'down'
  const fitButtonRef = React.useRef(null);

  const displayEmail = isSpectatorMode ? maskEmail(buyer.email) : buyer.email;
  const displayScore = buyer.interestScore ?? buyer.score ?? 0;
  const fitPercentage = buyer.profile?.fitPercentage;
  const timeInfo = buyer.lastVisit ? getTimeInfo(buyer.lastVisit) : null;
  const profile = buyer.profile;

  // Cerrar popover al hacer clic fuera
  React.useEffect(() => {
    if (!showFitPopover) return;

    const handleClickOutside = (e) => {
      if (fitButtonRef.current && !fitButtonRef.current.contains(e.target)) {
        setShowFitPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFitPopover]);

  // Estado de conversión
  const conversionStatus = buyer.hasBid ? 'bid' :
    buyer.conversionStatus === 'attempted' ? 'attempted' :
      buyer.visits > 0 ? 'watching' : 'none';

  // Completitud del perfil
  const getProfileCompleteness = () => {
    if (!profile) return { level: 'empty', icon: UserX, color: 'text-slate-600', bg: 'bg-slate-700/30' };

    const hasFinancial = profile.paymentMethod && profile.financingStatus && profile.propertyToSell;
    const hasFeatures = profile.minBedrooms || profile.minBathrooms || profile.desiredSqm ||
      profile.elevator || profile.parking || profile.outdoorSpace;

    if (hasFinancial && hasFeatures) {
      return { level: 'complete', icon: UserCheck, color: 'text-green-400', bg: 'bg-green-500/20' };
    } else if (hasFinancial || hasFeatures) {
      return { level: 'partial', icon: UserMinus, color: 'text-amber-400', bg: 'bg-amber-500/20' };
    }
    return { level: 'empty', icon: UserX, color: 'text-slate-600', bg: 'bg-slate-700/30' };
  };

  const profileStatus = getProfileCompleteness();
  const ProfileIcon = profileStatus.icon;

  // Color del score
  const getScoreStyle = (score) => {
    if (score >= 85) return { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-400' };
    if (score >= 60) return { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400' };
    if (score >= 40) return { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400' };
    if (score >= 20) return { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400' };
    return { bg: 'bg-slate-700/50', border: 'border-slate-600/40', text: 'text-slate-400' };
  };

  const scoreStyle = getScoreStyle(displayScore);

  // Color del fit - ROJO si hay deal-breaker (required que falla), independiente del %
  const getFitStyle = (fit, hasMismatch) => {
    if (hasMismatch) return 'text-red-400 bg-red-500/20 border-red-500/30';
    if (fit >= 80) return 'text-green-400 bg-green-500/20 border-green-500/30';
    if (fit >= 50) return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
    return 'text-red-400 bg-red-500/20 border-red-500/30';
  };

  // Calcular fit al vuelo cuando se hace clic en el botón de info
  const handleFitInfoClick = (e) => {
    e.stopPropagation(); // No abrir el modal del comprador

    // Calcular dirección del popover según posición en pantalla
    if (fitButtonRef.current) {
      const rect = fitButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Si hay menos de 250px abajo, abrir hacia arriba
      setPopoverDirection(spaceBelow < 250 && spaceAbove > spaceBelow ? 'up' : 'down');
    }

    if (!profile || !propertyMetadata) {
      setFitCalculation({ error: 'Sin datos suficientes' });
      setShowFitPopover(true);
      return;
    }

    // Calcular al vuelo
    const result = calculateFitPercentage(profile, propertyMetadata);
    setFitCalculation({
      ...result,
      propertyData: propertyMetadata,
      buyerData: profile
    });
    setShowFitPopover(true);
  };

  const closeFitPopover = (e) => {
    e.stopPropagation();
    setShowFitPopover(false);
  };

  // Determinar si hay mismatch para el color (calcular ligeramente)
  const hasMismatch = useMemo(() => {
    if (!profile || !propertyMetadata) return false;
    const { hasRequiredMismatch } = calculateFitPercentage(profile, propertyMetadata);
    return hasRequiredMismatch;
  }, [profile, propertyMetadata]);

  // Formatear tiempo
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  return (
    <div
      onClick={onViewDetail}
      className="bg-slate-800/50 hover:bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-600 transition-all cursor-pointer group px-4 py-2.5"
    >
      {/* Layout: fluido - gap-4 en móvil/tablet, gap-6 en desktop */}
      <div className="flex items-center gap-4 lg:gap-6 flex-wrap">

        {/* RANKING */}
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
          <span className="text-sm font-bold text-slate-300">#{rank}</span>
        </div>

        {/* COMPRADOR (nombre + email) */}
        <div className="min-w-[180px]">
          <div className="text-white font-semibold truncate">{buyer.name}</div>
          <div className="text-sm text-slate-400 truncate">{displayEmail}</div>
        </div>

        {/* ESTADO */}
        <div className="min-w-[90px]">
          <StatusBadge status={conversionStatus} hasSubmitAttempt={buyer.hasSubmitAttempt} />
        </div>

        {/* Separador visual */}
        <div className="hidden lg:block w-px h-10 bg-slate-600 -mx-1" />

        {/* INTERÉS (Score) - con color */}
        <div className="flex flex-col items-center min-w-[70px]">
          <div className={`px-3 py-1.5 rounded-lg ${scoreStyle.bg} border ${scoreStyle.border}`}>
            <span className={`text-xl font-bold ${scoreStyle.text}`}>{displayScore}</span>
          </div>
          <span className="text-xs text-slate-500 mt-1">Interés</span>
        </div>

        {/* MÉTRICAS: Visitas, Días, Tiempo, Eventos - iconos grises */}
        <div className="flex items-center gap-4 lg:gap-6">
          {/* Visitas */}
          <div className="flex flex-col items-center w-[60px] flex-shrink-0">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Eye className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{buyer.visits || 0}</span>
            </div>
            <span className="text-xs text-slate-500">visitas</span>
          </div>

          {/* Días */}
          <div className="flex flex-col items-center w-[50px] flex-shrink-0">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{buyer.uniqueDays || 0}</span>
            </div>
            <span className="text-xs text-slate-500">días</span>
          </div>

          {/* Tiempo: OCULTO - No confiable sin control de visibilidad */}
          {/*
          <div className="flex flex-col items-center w-[55px] flex-shrink-0">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{formatTime(buyer.totalTime)}</span>
            </div>
            <span className="text-xs text-slate-500">tiempo</span>
          </div>
          */}

          {/* Eventos */}
          <div className="flex flex-col items-center w-[60px] flex-shrink-0">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Activity className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{buyer.eventCount || 0}</span>
            </div>
            <span className="text-xs text-slate-500">eventos</span>
          </div>
        </div>

        {/* ÚLTIMA VISITA - con color */}
        <div className="flex flex-col items-center min-w-[100px]">
          {timeInfo ? (
            <>
              <span className={`font-semibold ${timeInfo.colorClass}`}>
                {timeInfo.timeAgo}
              </span>
              <span className="text-xs text-slate-400">
                {timeInfo.shortDate} {timeInfo.time}
              </span>
            </>
          ) : (
            <span className="text-slate-600 text-sm">Sin visitas</span>
          )}
        </div>

        {/* INTENTOS Y PUJAS */}
        <div className="flex items-center gap-4 lg:gap-6">
          {/* Intentos */}
          <div className="flex flex-col items-center min-w-[50px]">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Target className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{buyer.biddingAttempts || 0}</span>
            </div>
            <span className="text-xs text-slate-500">intentos</span>
          </div>

          {/* Pujas (exitosas) con valor */}
          <div className="flex flex-col items-center min-w-[60px]">
            <div className="flex items-center gap-1.5 text-slate-300">
              <Trophy className="w-4 h-4 text-slate-500" />
              <span className="font-semibold">{buyer.bidsCount || 0}</span>
            </div>
            {buyer.maxBidAmount ? (
              <span className="text-xs text-slate-400">
                {(buyer.maxBidAmount / 1000).toFixed(0)}k€
              </span>
            ) : (
              <span className="text-xs text-slate-500">pujas</span>
            )}
          </div>
        </div>

        {/* Separador visual antes de perfil */}
        <div className="hidden lg:block w-px h-10 bg-slate-600 -mx-1" />

        {/* Fit % - con color e info clicable */}
        <div className="flex flex-col items-center min-w-[55px] relative" ref={fitButtonRef}>
          {fitPercentage !== null && fitPercentage !== undefined ? (
            <span className={`px-2 py-1 rounded border text-sm font-bold ${getFitStyle(fitPercentage, hasMismatch)}`}>
              {fitPercentage}%
            </span>
          ) : (
            <span className="text-slate-600 text-sm">—</span>
          )}
          <button
            onClick={handleFitInfoClick}
            className="flex items-center gap-1 mt-1 hover:bg-slate-700/50 rounded px-1 py-0.5 transition-colors"
          >
            <span className="text-xs text-slate-500">Fit</span>
            <Info className="w-3 h-3 text-slate-500 hover:text-amber-400" />
          </button>

          {/* Popover clicable - dirección dinámica */}
          {showFitPopover && (
            <div className={`absolute left-1/2 -translate-x-1/2 z-50 ${
              popoverDirection === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}>
              {/* Flecha */}
              <div className={`absolute left-1/2 -translate-x-1/2 rotate-45 w-2 h-2 bg-slate-900 ${
                popoverDirection === 'up'
                  ? 'bottom-[-5px] border-r border-b border-slate-600'
                  : '-top-1 border-l border-t border-slate-600'
              }`} />
              <div className="bg-slate-900 border border-slate-600 rounded-lg p-3 shadow-xl w-72">
                {/* Header con botón cerrar */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-amber-400">Desglose del Fit</p>
                  <button onClick={closeFitPopover} className="text-slate-400 hover:text-white p-0.5">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {fitCalculation?.error ? (
                  <p className="text-xs text-slate-400">{fitCalculation.error}</p>
                ) : fitCalculation?.matchDetails ? (
                  <>
                    {/* Matches - criterios cumplidos */}
                    {fitCalculation.matchDetails.matches?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-green-400 font-medium mb-1">Cumple:</p>
                        <div className="space-y-1">
                          {fitCalculation.matchDetails.matches.map((m, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-green-500/10 rounded px-2 py-1">
                              <span className="text-green-400">✓</span>
                              <span className="text-slate-300 font-medium">{m.label}:</span>
                              <span className="text-slate-400">busca {m.buyer}</span>
                              <span className="text-slate-500">→</span>
                              <span className="text-green-400">{m.property}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warnings - criterios con advertencia (ej: m² cerca pero no exacto) */}
                    {fitCalculation.matchDetails.warnings?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-amber-400 font-medium mb-1">Advertencia:</p>
                        <div className="space-y-1">
                          {fitCalculation.matchDetails.warnings.map((m, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-amber-500/10 rounded px-2 py-1">
                              <span className="text-amber-400">⚠</span>
                              <span className="text-slate-300 font-medium">{m.label}:</span>
                              <span className="text-slate-400">busca {m.buyer}</span>
                              <span className="text-slate-500">→</span>
                              <span className="text-amber-400">{m.property}</span>
                              {m.difference && <span className="text-amber-500 text-[10px]">(-{m.difference}m²)</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mismatches - criterios que fallan */}
                    {fitCalculation.matchDetails.mismatches?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-red-400 font-medium mb-1">No cumple:</p>
                        <div className="space-y-1">
                          {fitCalculation.matchDetails.mismatches.map((m, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-red-500/10 rounded px-2 py-1">
                              <span className="text-red-400">✗</span>
                              <span className="text-slate-300 font-medium">{m.label}:</span>
                              <span className="text-slate-400">busca {m.buyer}</span>
                              <span className="text-slate-500">→</span>
                              <span className="text-red-400">{m.property}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Footer en 2 columnas: texto + resultado */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                      <p className="text-[10px] text-slate-500 flex-1">
                        Solo imprescindibles. "Deseables" no afectan.
                      </p>
                      <span className={`px-1.5 py-0.5 rounded border text-xs font-bold ml-2 ${getFitStyle(fitCalculation.fitPercentage, fitCalculation.hasRequiredMismatch)}`}>
                        {fitCalculation.fitPercentage}% ({fitCalculation.matchDetails.matchedCriteria}/{fitCalculation.matchDetails.totalCriteria})
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">
                    Sin datos suficientes. El comprador debe completar su perfil de búsqueda.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Icono completitud perfil */}
        <div className="flex flex-col items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${profileStatus.bg}`}
            title={`Perfil: ${profileStatus.level === 'complete' ? 'Completo' : profileStatus.level === 'partial' ? 'Parcial' : 'Vacío'}`}
          >
            <ProfileIcon className={`w-4 h-4 ${profileStatus.color}`} />
          </div>
          <span className="text-xs text-slate-500 mt-1">Perfil</span>
        </div>

        {/* Datos financieros - colores según riesgo */}
        {profile ? (
          <div className="flex items-center gap-3">
            {/* Pago */}
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm ${getPaymentColor(profile.paymentMethod)}`}>
              <CreditCard className="w-3.5 h-3.5" />
              <span className="font-medium">
                {LABELS.paymentMethod[profile.paymentMethod] || '-'}
              </span>
            </div>
            {/* Financiación */}
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm ${getFinancingColor(profile.financingStatus)}`}>
              <Building className="w-3.5 h-3.5" />
              <span className="font-medium">{LABELS.financingStatus[profile.financingStatus] || '-'}</span>
            </div>
            {/* Vender */}
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm ${getPropertyToSellColor(profile.propertyToSell)}`}>
              <Home className="w-3.5 h-3.5" />
              <span className="font-medium">{LABELS.propertyToSell[profile.propertyToSell] || '-'}</span>
            </div>

            {/* Comentarios (tooltip) */}
            {profile.additionalComments && (
              <div className="relative group/tooltip">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-700/60 text-slate-400 cursor-help">
                  <MessageCircle className="w-4 h-4" />
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover/tooltip:block z-50">
                  <div className="bg-slate-900 border border-slate-600 rounded-lg p-3 shadow-xl max-w-xs">
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{profile.additionalComments}</p>
                  </div>
                  <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-900 border-r border-b border-slate-600" />
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className="text-slate-500 text-sm px-3 py-1.5 bg-slate-700/40 rounded-lg">Sin perfil</span>
        )}

        {/* FLECHA VER MÁS */}
        <div className="flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-5 h-5 text-amber-400" />
        </div>

      </div>
    </div>
  );
};

const StatusBadge = ({ status, hasSubmitAttempt }) => {
  const configs = {
    bid: {
      icon: CheckCircle,
      text: 'Pujó',
      classes: 'bg-green-500/20 border-green-500/50 text-green-400'
    },
    attempted: {
      icon: Flame,
      text: hasSubmitAttempt ? 'Intentó' : 'Exploró',
      classes: hasSubmitAttempt
        ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
        : 'bg-blue-500/20 border-blue-500/50 text-blue-400'
    },
    watching: {
      icon: Eye,
      text: 'Visitó',
      classes: 'bg-slate-600/30 border-slate-500/50 text-slate-300'
    },
    none: {
      icon: Clock,
      text: 'Inactivo',
      classes: 'bg-slate-700/30 border-slate-600/50 text-slate-500'
    }
  };

  const config = configs[status] || configs.none;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.classes}`}>
      <Icon className="w-3 h-3" />
      <span>{config.text}</span>
    </span>
  );
};

export default BuyersView;
